import { Inject, Injectable } from '@angular/core';
import {
  collection,
  deleteDoc,
  doc,
  Firestore,
  getDoc,
  limit,
  orderBy,
  query,
  startAfter,
  documentId,
  getDocs,
  where,
  onSnapshot,
} from 'firebase/firestore';
import { forkJoin, from, map, Observable, of } from 'rxjs';
import { Deck, deckConverter } from '../models/deck.model';
import { FIRESTORE } from '../firebase-injection';

@Injectable({
  providedIn: 'root',
})
export class PublicDeckService {
  constructor(@Inject(FIRESTORE) private firestore: Firestore) {}

  deletePublicDeck(deckId: string) {
    const publicDeckRef = doc(this.firestore, `public-decks/${deckId}`);
    return deleteDoc(publicDeckRef);
  }

  getPublicDecks(pageIndex: number, pageSize: number) {
    return new Observable<Deck[]>((subscriber) => {
      const q = query(
        collection(this.firestore, 'public-decks').withConverter(deckConverter),
        orderBy('name'),
        startAfter(pageIndex * pageSize),
        limit(pageSize)
      );
      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const decks = snapshot.docs
            .filter((doc) => doc.exists())
            .map((doc) => doc.data() as Deck);
          subscriber.next(decks);
        },
        (error) => subscriber.error(error)
      );
      return { unsubscribe };
    });
  }

  getPublicDecksByIDs(IDs: string[]): Observable<Deck[]> {
    if (IDs.length === 0) {
      return of([]);
    }

    const CHUNK_SIZE = 30;
    const chunks: string[][] = [];

    for (let i = 0; i < IDs.length; i += CHUNK_SIZE) {
      chunks.push(IDs.slice(i, i + CHUNK_SIZE));
    }

    const queries$ = chunks.map((chunk) => {
      const publicDecksCollection = collection(
        this.firestore,
        'public-decks'
      ).withConverter(deckConverter);

      const q = query(publicDecksCollection, where(documentId(), 'in', chunk));

      return from(getDocs(q)).pipe(
        map((snapshot) => snapshot.docs.map((doc) => doc.data()))
      );
    });

    return (
      queries$.length === 1
        ? queries$[0]
        : forkJoin(queries$).pipe(map((results) => results.flat()))
    ).pipe(
      map((decks) => {
        // Sortiere nach der Original-Reihenfolge der IDs
        const deckMap = new Map(
          decks
            .filter((deck): deck is Deck => deck !== null && deck !== undefined)
            .map((deck) => [deck.id, deck])
        );
        return IDs.map((id) => deckMap.get(id)).filter(
          (deck): deck is Deck => deck !== undefined
        );
      })
    );
  }

  getPublicDeck(deckId: string): Observable<Deck | null> {
    const publicDeckRef = doc(
      this.firestore,
      `public-decks/${deckId}`
    ).withConverter(deckConverter);
    return from(getDoc(publicDeckRef)).pipe(
      map((docSnap): Deck | null => {
        if (docSnap.exists()) {
          return docSnap.data() as Deck;
        }
        return null;
      })
    );
  }
}
